import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "../chat/Markdown";
import { useChat } from "../state";

interface ProjectChatProps {
  projectId: number;
}

export const ProjectChat = ({ projectId }: ProjectChatProps) => {
  const { messages, isStreaming, sendMessage, clearChat } = useChat();
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = useMemo(() => input.trim(), [input]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming]);

  useEffect(() => {
    clearChat();
  }, [projectId, clearChat]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed || isStreaming) return;
    setInput("");
    await sendMessage(projectId, trimmed);
  };

  return (
    <div className="border-t p-2 flex flex-col gap-2 flex-1 min-h-0">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Chat</p>
      <div className="bg-gray-50 border border-gray-200 rounded p-2 flex-1 min-h-0 overflow-y-auto space-y-2">
        {messages.length === 0 ? (
          <p
            className="text-xs text-gray-400 cursor-text"
            onClick={() => inputRef.current?.focus()}
          >
            Stel een vraag over je percelen, bijvoorbeeld over adressen of kenmerken.
          </p>
        ) : (
          messages.map((message, idx) => (
            <div key={idx}>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">
                {message.role === "user" ? "Jij" : "Assistent"}
              </p>
              <div className="text-xs text-black wrap-break-word">
                {message.role === "user" ? (
                  <p className="whitespace-pre-wrap">{message.content}</p>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {message.content}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={onSubmit} className="flex gap-1 shrink-0">
        <input
          ref={inputRef}
          className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs"
          placeholder="Typ je vraag..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isStreaming}
        />
        <button
          type="submit"
          className="px-2 py-1 bg-bright-yellow hover:bg-yellow rounded text-xs disabled:opacity-50"
          disabled={!trimmed || isStreaming}
        >
          {isStreaming ? "..." : "Send"}
        </button>
      </form>
    </div>
  );
};
